import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

interface ITooltipWithOverflowProps {
  text: string;
  copyable?: boolean;
  maxWidth?: number | string;
}

export const TooltipWithOverflow: FC<ITooltipWithOverflowProps> = ({
  text,
  copyable = false,
  maxWidth,
}) => {
  const { t } = useTranslation('common.TooltipWithOverflow');

  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    const hasOverflow = element.scrollWidth > element.clientWidth + 1;
    setIsOverflowing(hasOverflow);
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
  }, [text, checkOverflow]);

  useEffect(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(element);
    window.addEventListener('resize', checkOverflow);

    const timeoutId = setTimeout(checkOverflow, 100);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [checkOverflow]);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        message.success(t('copied'));
        setTimeout(() => setIsCopied(false), 2000);
      } catch {
        message.error(t('failedToCopy'));
      }
    },
    [text, t]
  );

  const tooltipContent = (
    <span
      onClick={copyable ? handleCopy : undefined}
      className={copyable ? styles.tooltipContent : undefined}
    >
      {text}
      {copyable && (
        <span className={styles.copyIconWrapper}>
          {isCopied ? (
            <CheckOutlined className={styles.checkIcon} />
          ) : (
            <CopyOutlined className={styles.copyIcon} />
          )}
        </span>
      )}
    </span>
  );

  const textElement = (
    <span
      ref={textRef}
      className={styles.truncatedText}
      style={maxWidth ? { maxWidth } : undefined}
    >
      {text}
    </span>
  );

  if (!isOverflowing) {
    return textElement;
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      overlayClassName={styles.tooltipOverlay}
    >
      {textElement}
    </Tooltip>
  );
};
